import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;
const { TabPane } = Tabs;

type HotelProperty = {
  id: number;
  name: string;
  code: string;
  warehouse_id?: number | null;
  warehouse_name?: string | null;
  currency_id: number;
  currency_code?: string | null;
  currency_symbol?: string | null;
  star_rating?: number | null;
  timezone?: string | null;
  check_in_time?: string | null;
  check_out_time?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  postal_code?: string | null;
  is_active?: number | boolean | null;
};

type Warehouse = {
  id: number;
  name: string;
};

type Currency = {
  id: number;
  code: string;
  symbol?: string | null;
  name: string;
};

type HotelBuilding = {
  id: number;
  property_id: number;
  property_name?: string | null;
  name: string;
  code: string;
  description?: string | null;
  is_active?: number | boolean | null;
};

type HotelFloor = {
  id: number;
  property_id: number;
  property_name?: string | null;
  building_id?: number | null;
  building_name?: string | null;
  floor_number: number;
  name?: string | null;
  is_active?: number | boolean | null;
};

type HotelRoomType = {
  id: number;
  property_id: number;
  property_name?: string | null;
  code: string;
  name: string;
  description?: string | null;
  base_occupancy?: number | null;
  max_occupancy?: number | null;
  max_adults?: number | null;
  max_children?: number | null;
  bed_type?: string | null;
  room_size?: number | null;
  view_type?: string | null;
  smoking_allowed?: number | boolean | null;
  accessible?: number | boolean | null;
  default_rate?: number | null;
  is_active?: number | boolean | null;
};

type HotelAmenity = {
  id: number;
  name: string;
  category?: string | null;
  description?: string | null;
  is_active?: number | boolean | null;
};

type PropertyFormValues = {
  name: string;
  code: string;
  warehouseId?: number;
  currencyId: number;
  starRating?: number;
  timezone?: string;
  checkInTime?: string;
  checkOutTime?: string;
  email?: string;
  phone?: string;
  address?: string;
  city?: string;
  country?: string;
  postalCode?: string;
  isActive?: boolean;
};

type BuildingFormValues = {
  propertyId: number;
  name: string;
  code: string;
  description?: string;
  isActive?: boolean;
};

type FloorFormValues = {
  propertyId: number;
  buildingId?: number;
  floorNumber: number;
  name?: string;
  isActive?: boolean;
};

type RoomTypeFormValues = {
  propertyId: number;
  code: string;
  name: string;
  description?: string;
  baseOccupancy?: number;
  maxOccupancy?: number;
  maxAdults?: number;
  maxChildren?: number;
  bedType?: string;
  roomSize?: number;
  viewType?: string;
  smokingAllowed?: boolean;
  accessible?: boolean;
  defaultRate?: number;
  isActive?: boolean;
  amenityIds?: number[];
};

type AmenityFormValues = {
  name: string;
  category?: string;
  description?: string;
  isActive?: boolean;
};

const ConfigurationTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [buildings, setBuildings] = useState<HotelBuilding[]>([]);
  const [floors, setFloors] = useState<HotelFloor[]>([]);
  const [roomTypes, setRoomTypes] = useState<HotelRoomType[]>([]);
  const [amenities, setAmenities] = useState<HotelAmenity[]>([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [loadingFloors, setLoadingFloors] = useState(false);
  const [loadingRoomTypes, setLoadingRoomTypes] = useState(false);
  const [loadingAmenities, setLoadingAmenities] = useState(false);

  const [propertyFilters, setPropertyFilters] = useState({
    isActive: "all" as "all" | "true" | "false",
    search: "",
  });
  const [buildingFilters, setBuildingFilters] = useState({
    propertyId: "all" as number | "all",
    isActive: "all" as "all" | "true" | "false",
  });
  const [floorFilters, setFloorFilters] = useState({
    propertyId: "all" as number | "all",
    buildingId: "all" as number | "all",
    isActive: "all" as "all" | "true" | "false",
  });
  const [roomTypeFilters, setRoomTypeFilters] = useState({
    propertyId: "all" as number | "all",
    isActive: "all" as "all" | "true" | "false",
  });
  const [amenityFilters, setAmenityFilters] = useState({
    isActive: "all" as "all" | "true" | "false",
    search: "",
  });

  const [propertyModalOpen, setPropertyModalOpen] = useState(false);
  const [buildingModalOpen, setBuildingModalOpen] = useState(false);
  const [floorModalOpen, setFloorModalOpen] = useState(false);
  const [roomTypeModalOpen, setRoomTypeModalOpen] = useState(false);
  const [amenityModalOpen, setAmenityModalOpen] = useState(false);

  const [editingProperty, setEditingProperty] = useState<HotelProperty | null>(null);
  const [editingBuilding, setEditingBuilding] = useState<HotelBuilding | null>(null);
  const [editingFloor, setEditingFloor] = useState<HotelFloor | null>(null);
  const [editingRoomType, setEditingRoomType] = useState<HotelRoomType | null>(null);
  const [editingAmenity, setEditingAmenity] = useState<HotelAmenity | null>(null);

  const [propertyForm] = Form.useForm<PropertyFormValues>();
  const [buildingForm] = Form.useForm<BuildingFormValues>();
  const [floorForm] = Form.useForm<FloorFormValues>();
  const [roomTypeForm] = Form.useForm<RoomTypeFormValues>();
  const [amenityForm] = Form.useForm<AmenityFormValues>();
  const floorPropertyId = Form.useWatch("propertyId", floorForm);

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);

  const canViewProperties = Boolean(
    hasPermission?.("Hotellerie.Configuration.Proprietes.view")
  );
  const canCreateProperties = Boolean(
    hasPermission?.("Hotellerie.Configuration.Proprietes.create")
  );
  const canEditProperties = Boolean(
    hasPermission?.("Hotellerie.Configuration.Proprietes.edit")
  );
  const canDeleteProperties = Boolean(
    hasPermission?.("Hotellerie.Configuration.Proprietes.delete")
  );

  const canViewBuildings = Boolean(
    hasPermission?.("Hotellerie.Configuration.Batiments.view")
  );
  const canCreateBuildings = Boolean(
    hasPermission?.("Hotellerie.Configuration.Batiments.create")
  );
  const canEditBuildings = Boolean(
    hasPermission?.("Hotellerie.Configuration.Batiments.edit")
  );
  const canDeleteBuildings = Boolean(
    hasPermission?.("Hotellerie.Configuration.Batiments.delete")
  );

  const canViewFloors = Boolean(
    hasPermission?.("Hotellerie.Configuration.Etages.view")
  );
  const canCreateFloors = Boolean(
    hasPermission?.("Hotellerie.Configuration.Etages.create")
  );
  const canEditFloors = Boolean(
    hasPermission?.("Hotellerie.Configuration.Etages.edit")
  );
  const canDeleteFloors = Boolean(
    hasPermission?.("Hotellerie.Configuration.Etages.delete")
  );

  const canViewRoomTypes = Boolean(
    hasPermission?.("Hotellerie.Configuration.TypesChambres.view")
  );
  const canCreateRoomTypes = Boolean(
    hasPermission?.("Hotellerie.Configuration.TypesChambres.create")
  );
  const canEditRoomTypes = Boolean(
    hasPermission?.("Hotellerie.Configuration.TypesChambres.edit")
  );
  const canDeleteRoomTypes = Boolean(
    hasPermission?.("Hotellerie.Configuration.TypesChambres.delete")
  );

  const canViewAmenities = Boolean(
    hasPermission?.("Hotellerie.Configuration.Amenites.view")
  );
  const canCreateAmenities = Boolean(
    hasPermission?.("Hotellerie.Configuration.Amenites.create")
  );
  const canEditAmenities = Boolean(
    hasPermission?.("Hotellerie.Configuration.Amenites.edit")
  );
  const canDeleteAmenities = Boolean(
    hasPermission?.("Hotellerie.Configuration.Amenites.delete")
  );

  const fetchWarehouses = useCallback(async () => {
    if (requiresCompanySelection || !selectedCompany) {
      setWarehouses([]);
      return;
    }
    try {
      const response = await axios.get(`${API_CONFIG.API_URL}/warehouses`, {
        params: { company_id: selectedCompany, page: 1, limit: 1000 },
      });
      setWarehouses(response.data.warehouses || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching warehouses:", error);
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchCurrencies = useCallback(async () => {
    if (requiresCompanySelection) {
      setCurrencies([]);
      return;
    }
    try {
      const params: Record<string, string | number> = { page: 1, limit: 200 };
      if (selectedCompany) {
        params.companyId = selectedCompany;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/currencies`, {
        params,
      });
      setCurrencies(response.data.currencies || []);
    } catch (error: any) {
      console.error("[HOTEL] Error fetching currencies:", error);
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection || !canViewProperties) {
      return;
    }
    setLoadingProperties(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (propertyFilters.isActive !== "all") {
        params.is_active = propertyFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    } finally {
      setLoadingProperties(false);
    }
  }, [canViewProperties, propertyFilters.isActive, requiresCompanySelection, selectedCompany]);

  const fetchBuildings = useCallback(async () => {
    if (requiresCompanySelection || !canViewBuildings) {
      return;
    }
    setLoadingBuildings(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (buildingFilters.propertyId !== "all") {
        params.property_id = buildingFilters.propertyId;
      }
      if (buildingFilters.isActive !== "all") {
        params.is_active = buildingFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/buildings`, {
        params,
      });
      if (response.data.success) {
        setBuildings(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching buildings:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des batiments"
      );
    } finally {
      setLoadingBuildings(false);
    }
  }, [buildingFilters.isActive, buildingFilters.propertyId, canViewBuildings, requiresCompanySelection, selectedCompany]);

  const fetchFloors = useCallback(async () => {
    if (requiresCompanySelection || !canViewFloors) {
      return;
    }
    setLoadingFloors(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (floorFilters.propertyId !== "all") {
        params.property_id = floorFilters.propertyId;
      }
      if (floorFilters.buildingId !== "all") {
        params.building_id = floorFilters.buildingId;
      }
      if (floorFilters.isActive !== "all") {
        params.is_active = floorFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/floors`, {
        params,
      });
      if (response.data.success) {
        setFloors(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching floors:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des etages"
      );
    } finally {
      setLoadingFloors(false);
    }
  }, [floorFilters.buildingId, floorFilters.isActive, floorFilters.propertyId, canViewFloors, requiresCompanySelection, selectedCompany]);

  const fetchRoomTypes = useCallback(async () => {
    if (requiresCompanySelection || !canViewRoomTypes) {
      return;
    }
    setLoadingRoomTypes(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (roomTypeFilters.propertyId !== "all") {
        params.property_id = roomTypeFilters.propertyId;
      }
      if (roomTypeFilters.isActive !== "all") {
        params.is_active = roomTypeFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/room-types`, {
        params,
      });
      if (response.data.success) {
        setRoomTypes(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching room types:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors du chargement des types de chambre"
      );
    } finally {
      setLoadingRoomTypes(false);
    }
  }, [canViewRoomTypes, requiresCompanySelection, roomTypeFilters.isActive, roomTypeFilters.propertyId, selectedCompany]);

  const fetchAmenities = useCallback(async () => {
    if (requiresCompanySelection || !canViewAmenities) {
      return;
    }
    setLoadingAmenities(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (amenityFilters.isActive !== "all") {
        params.is_active = amenityFilters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/amenities`, {
        params,
      });
      if (response.data.success) {
        setAmenities(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching amenities:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des amenites"
      );
    } finally {
      setLoadingAmenities(false);
    }
  }, [amenityFilters.isActive, canViewAmenities, requiresCompanySelection, selectedCompany]);

  useEffect(() => {
    fetchCurrencies();
    fetchWarehouses();
  }, [fetchCurrencies, fetchWarehouses]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchBuildings();
  }, [fetchBuildings]);

  useEffect(() => {
    fetchFloors();
  }, [fetchFloors]);

  useEffect(() => {
    fetchRoomTypes();
  }, [fetchRoomTypes]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const propertyOptions = useMemo(
    () =>
      properties.map((property) => ({
        value: property.id,
        label: property.name,
      })),
    [properties]
  );

  const buildingOptions = useMemo(
    () =>
      buildings.map((building) => ({
        value: building.id,
        label: building.name,
        propertyId: building.property_id,
      })),
    [buildings]
  );

  const buildingOptionsForProperty = (propertyId?: number) =>
    buildingOptions.filter((option) =>
      propertyId ? Number(option.propertyId) === Number(propertyId) : true
    );

  const filteredProperties = useMemo(() => {
    const search = propertyFilters.search.trim().toLowerCase();
    if (!search) {
      return properties;
    }
    return properties.filter((property) =>
      [property.name, property.code, property.city, property.country]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [properties, propertyFilters.search]);

  const filteredAmenities = useMemo(() => {
    const search = amenityFilters.search.trim().toLowerCase();
    if (!search) {
      return amenities;
    }
    return amenities.filter((amenity) =>
      [amenity.name, amenity.category]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [amenities, amenityFilters.search]);

  const fetchRoomTypeAmenities = useCallback(
    async (roomTypeId: number) => {
      if (requiresCompanySelection) {
        return [] as number[];
      }
      try {
        const params = selectedCompany ? { company_id: selectedCompany } : {};
        const response = await axios.get(
          `${API_CONFIG.API_URL}/hotel/amenities/room-types/${roomTypeId}`,
          { params }
        );
        if (response.data.success) {
          return (response.data.data || []).map((amenity: HotelAmenity) => amenity.id);
        }
      } catch (error: any) {
        console.error("[HOTEL] Error fetching room type amenities:", error);
      }
      return [] as number[];
    },
    [requiresCompanySelection, selectedCompany]
  );

  const saveRoomTypeAmenities = useCallback(
    async (roomTypeId: number, amenityIds: number[]) => {
      try {
        const payload = {
          ...(selectedCompany ? { company_id: selectedCompany } : {}),
          amenity_ids: amenityIds,
        };
        await axios.put(
          `${API_CONFIG.API_URL}/hotel/amenities/room-types/${roomTypeId}`,
          payload
        );
      } catch (error: any) {
        console.error("[HOTEL] Error saving room type amenities:", error);
      }
    },
    [selectedCompany]
  );

  const openCreateProperty = () => {
    setEditingProperty(null);
    propertyForm.resetFields();
    propertyForm.setFieldsValue({
      timezone: "UTC",
      checkInTime: "14:00:00",
      checkOutTime: "12:00:00",
      isActive: true,
      starRating: 0,
    });
    setPropertyModalOpen(true);
  };

  const openEditProperty = (property: HotelProperty) => {
    setEditingProperty(property);
    propertyForm.setFieldsValue({
      name: property.name,
      code: property.code,
      warehouseId: property.warehouse_id || undefined,
      currencyId: property.currency_id,
      starRating: property.star_rating || 0,
      timezone: property.timezone || "UTC",
      checkInTime: property.check_in_time || "14:00:00",
      checkOutTime: property.check_out_time || "12:00:00",
      email: property.email || undefined,
      phone: property.phone || undefined,
      address: property.address || undefined,
      city: property.city || undefined,
      country: property.country || undefined,
      postalCode: property.postal_code || undefined,
      isActive: Boolean(property.is_active),
    });
    setPropertyModalOpen(true);
  };

  const handleSaveProperty = async () => {
    const values = await propertyForm.validateFields();
    if (!editingProperty && !canCreateProperties) {
      message.warning("Vous n'avez pas les droits pour creer une propriete.");
      return;
    }
    if (editingProperty && !canEditProperties) {
      message.warning("Vous n'avez pas les droits pour modifier cette propriete.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      name: values.name,
      code: values.code,
      warehouse_id: values.warehouseId || null,
      currency_id: values.currencyId,
      star_rating: values.starRating ?? 0,
      timezone: values.timezone || "UTC",
      check_in_time: values.checkInTime || "14:00:00",
      check_out_time: values.checkOutTime || "12:00:00",
      email: values.email || null,
      phone: values.phone || null,
      address: values.address || null,
      city: values.city || null,
      country: values.country || null,
      postal_code: values.postalCode || null,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingProperty) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/properties/${editingProperty.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Propriete mise a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/properties`,
          payload
        );
        if (response.data.success) {
          message.success("Propriete creee.");
        }
      }
      setPropertyModalOpen(false);
      setEditingProperty(null);
      fetchProperties();
    } catch (error: any) {
      console.error("[HOTEL] Error saving property:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de la propriete"
      );
    }
  };

  const confirmDeleteProperty = (property: HotelProperty) => {
    if (!canDeleteProperties) {
      message.warning("Vous n'avez pas les droits pour supprimer cette propriete.");
      return;
    }
    Modal.confirm({
      title: "Supprimer la propriete",
      content: `Confirmer la suppression de ${property.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/properties/${property.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Propriete supprimee.");
            fetchProperties();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting property:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de la propriete"
          );
        }
      },
    });
  };

  const openCreateBuilding = () => {
    setEditingBuilding(null);
    buildingForm.resetFields();
    buildingForm.setFieldsValue({
      propertyId: buildingFilters.propertyId !== "all" ? buildingFilters.propertyId : undefined,
      isActive: true,
    });
    setBuildingModalOpen(true);
  };

  const openEditBuilding = (building: HotelBuilding) => {
    setEditingBuilding(building);
    buildingForm.setFieldsValue({
      propertyId: building.property_id,
      name: building.name,
      code: building.code,
      description: building.description || undefined,
      isActive: Boolean(building.is_active),
    });
    setBuildingModalOpen(true);
  };

  const handleSaveBuilding = async () => {
    const values = await buildingForm.validateFields();
    if (!editingBuilding && !canCreateBuildings) {
      message.warning("Vous n'avez pas les droits pour creer un batiment.");
      return;
    }
    if (editingBuilding && !canEditBuildings) {
      message.warning("Vous n'avez pas les droits pour modifier ce batiment.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      name: values.name,
      code: values.code,
      description: values.description || null,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingBuilding) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/buildings/${editingBuilding.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Batiment mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/buildings`,
          payload
        );
        if (response.data.success) {
          message.success("Batiment cree.");
        }
      }
      setBuildingModalOpen(false);
      setEditingBuilding(null);
      fetchBuildings();
    } catch (error: any) {
      console.error("[HOTEL] Error saving building:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du batiment"
      );
    }
  };

  const confirmDeleteBuilding = (building: HotelBuilding) => {
    if (!canDeleteBuildings) {
      message.warning("Vous n'avez pas les droits pour supprimer ce batiment.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le batiment",
      content: `Confirmer la suppression de ${building.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/buildings/${building.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Batiment supprime.");
            fetchBuildings();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting building:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du batiment"
          );
        }
      },
    });
  };

  const openCreateFloor = () => {
    setEditingFloor(null);
    floorForm.resetFields();
    floorForm.setFieldsValue({
      propertyId: floorFilters.propertyId !== "all" ? floorFilters.propertyId : undefined,
      isActive: true,
      floorNumber: 0,
    });
    setFloorModalOpen(true);
  };

  const openEditFloor = (floor: HotelFloor) => {
    setEditingFloor(floor);
    floorForm.setFieldsValue({
      propertyId: floor.property_id,
      buildingId: floor.building_id || undefined,
      floorNumber: floor.floor_number,
      name: floor.name || undefined,
      isActive: Boolean(floor.is_active),
    });
    setFloorModalOpen(true);
  };

  const handleSaveFloor = async () => {
    const values = await floorForm.validateFields();
    if (!editingFloor && !canCreateFloors) {
      message.warning("Vous n'avez pas les droits pour creer un etage.");
      return;
    }
    if (editingFloor && !canEditFloors) {
      message.warning("Vous n'avez pas les droits pour modifier cet etage.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      building_id: values.buildingId || null,
      floor_number: values.floorNumber,
      name: values.name || null,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingFloor) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/floors/${editingFloor.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Etage mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/floors`,
          payload
        );
        if (response.data.success) {
          message.success("Etage cree.");
        }
      }
      setFloorModalOpen(false);
      setEditingFloor(null);
      fetchFloors();
    } catch (error: any) {
      console.error("[HOTEL] Error saving floor:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de l'etage"
      );
    }
  };

  const confirmDeleteFloor = (floor: HotelFloor) => {
    if (!canDeleteFloors) {
      message.warning("Vous n'avez pas les droits pour supprimer cet etage.");
      return;
    }
    Modal.confirm({
      title: "Supprimer l'etage",
      content: `Confirmer la suppression de l'etage ${floor.floor_number} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/floors/${floor.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Etage supprime.");
            fetchFloors();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting floor:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de l'etage"
          );
        }
      },
    });
  };

  const openCreateRoomType = () => {
    setEditingRoomType(null);
    roomTypeForm.resetFields();
    roomTypeForm.setFieldsValue({
      propertyId: roomTypeFilters.propertyId !== "all" ? roomTypeFilters.propertyId : undefined,
      baseOccupancy: 1,
      maxOccupancy: 1,
      maxAdults: 1,
      maxChildren: 0,
      defaultRate: 0,
      isActive: true,
    });
    setRoomTypeModalOpen(true);
  };

  const openEditRoomType = async (roomType: HotelRoomType) => {
    setEditingRoomType(roomType);
    const amenityIds = await fetchRoomTypeAmenities(roomType.id);
    roomTypeForm.setFieldsValue({
      propertyId: roomType.property_id,
      code: roomType.code,
      name: roomType.name,
      description: roomType.description || undefined,
      baseOccupancy: roomType.base_occupancy || 1,
      maxOccupancy: roomType.max_occupancy || 1,
      maxAdults: roomType.max_adults || 1,
      maxChildren: roomType.max_children || 0,
      bedType: roomType.bed_type || undefined,
      roomSize: roomType.room_size || undefined,
      viewType: roomType.view_type || undefined,
      smokingAllowed: Boolean(roomType.smoking_allowed),
      accessible: Boolean(roomType.accessible),
      defaultRate: roomType.default_rate || 0,
      isActive: Boolean(roomType.is_active),
      amenityIds,
    });
    setRoomTypeModalOpen(true);
  };

  const handleSaveRoomType = async () => {
    const values = await roomTypeForm.validateFields();
    if (!editingRoomType && !canCreateRoomTypes) {
      message.warning("Vous n'avez pas les droits pour creer un type de chambre.");
      return;
    }
    if (editingRoomType && !canEditRoomTypes) {
      message.warning("Vous n'avez pas les droits pour modifier ce type de chambre.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      code: values.code,
      name: values.name,
      description: values.description || null,
      base_occupancy: values.baseOccupancy ?? 1,
      max_occupancy: values.maxOccupancy ?? 1,
      max_adults: values.maxAdults ?? 1,
      max_children: values.maxChildren ?? 0,
      bed_type: values.bedType || null,
      room_size: values.roomSize ?? null,
      view_type: values.viewType || null,
      smoking_allowed: values.smokingAllowed ? 1 : 0,
      accessible: values.accessible ? 1 : 0,
      default_rate: values.defaultRate ?? 0,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingRoomType) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/room-types/${editingRoomType.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Type de chambre mis a jour.");
          await saveRoomTypeAmenities(
            editingRoomType.id,
            values.amenityIds || []
          );
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/room-types`,
          payload
        );
        if (response.data.success) {
          message.success("Type de chambre cree.");
          const createdId = response.data.data?.id;
          if (createdId) {
            await saveRoomTypeAmenities(createdId, values.amenityIds || []);
          }
        }
      }
      setRoomTypeModalOpen(false);
      setEditingRoomType(null);
      fetchRoomTypes();
    } catch (error: any) {
      console.error("[HOTEL] Error saving room type:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du type de chambre"
      );
    }
  };

  const confirmDeleteRoomType = (roomType: HotelRoomType) => {
    if (!canDeleteRoomTypes) {
      message.warning("Vous n'avez pas les droits pour supprimer ce type de chambre.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le type de chambre",
      content: `Confirmer la suppression de ${roomType.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/room-types/${roomType.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Type de chambre supprime.");
            fetchRoomTypes();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting room type:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du type de chambre"
          );
        }
      },
    });
  };

  const openCreateAmenity = () => {
    setEditingAmenity(null);
    amenityForm.resetFields();
    amenityForm.setFieldsValue({
      isActive: true,
    });
    setAmenityModalOpen(true);
  };

  const openEditAmenity = (amenity: HotelAmenity) => {
    setEditingAmenity(amenity);
    amenityForm.setFieldsValue({
      name: amenity.name,
      category: amenity.category || undefined,
      description: amenity.description || undefined,
      isActive: Boolean(amenity.is_active),
    });
    setAmenityModalOpen(true);
  };

  const handleSaveAmenity = async () => {
    const values = await amenityForm.validateFields();
    if (!editingAmenity && !canCreateAmenities) {
      message.warning("Vous n'avez pas les droits pour creer une amenite.");
      return;
    }
    if (editingAmenity && !canEditAmenities) {
      message.warning("Vous n'avez pas les droits pour modifier cette amenite.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      name: values.name,
      category: values.category || null,
      description: values.description || null,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingAmenity) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/amenities/${editingAmenity.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Amenite mise a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/amenities`,
          payload
        );
        if (response.data.success) {
          message.success("Amenite creee.");
        }
      }
      setAmenityModalOpen(false);
      setEditingAmenity(null);
      fetchAmenities();
    } catch (error: any) {
      console.error("[HOTEL] Error saving amenity:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde de l'amenite"
      );
    }
  };

  const confirmDeleteAmenity = (amenity: HotelAmenity) => {
    if (!canDeleteAmenities) {
      message.warning("Vous n'avez pas les droits pour supprimer cette amenite.");
      return;
    }
    Modal.confirm({
      title: "Supprimer l'amenite",
      content: `Confirmer la suppression de ${amenity.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/amenities/${amenity.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Amenite supprimee.");
            fetchAmenities();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting amenity:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression de l'amenite"
          );
        }
      },
    });
  };

  const propertyColumns: ColumnsType<HotelProperty> = [
    {
      title: "Propriete",
      key: "property",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.code}</Text>
        </Space>
      ),
    },
    {
      title: "Devise",
      key: "currency",
      render: (_value, record) =>
        record.currency_symbol || record.currency_code || "-",
    },
    {
      title: "Établissement",
      dataIndex: "warehouse_name",
      key: "warehouse_name",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEditProperties ? (
            <Button type="link" onClick={() => openEditProperty(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteProperties ? (
            <Button type="link" danger onClick={() => confirmDeleteProperty(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const buildingColumns: ColumnsType<HotelBuilding> = [
    {
      title: "Batiment",
      key: "building",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.code}</Text>
        </Space>
      ),
    },
    {
      title: "Propriete",
      dataIndex: "property_name",
      key: "property_name",
      render: (value: string | null, record) =>
        value || `Propriete #${record.property_id}`,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEditBuildings ? (
            <Button type="link" onClick={() => openEditBuilding(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteBuildings ? (
            <Button type="link" danger onClick={() => confirmDeleteBuilding(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const floorColumns: ColumnsType<HotelFloor> = [
    {
      title: "Etage",
      key: "floor",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>Etage {record.floor_number}</Text>
          <Text type="secondary">{record.name || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Batiment",
      dataIndex: "building_name",
      key: "building_name",
      render: (value: string | null) => value || "-",
    },
    {
      title: "Propriete",
      dataIndex: "property_name",
      key: "property_name",
      render: (value: string | null, record) =>
        value || `Propriete #${record.property_id}`,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEditFloors ? (
            <Button type="link" onClick={() => openEditFloor(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteFloors ? (
            <Button type="link" danger onClick={() => confirmDeleteFloor(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const roomTypeColumns: ColumnsType<HotelRoomType> = [
    {
      title: "Type",
      key: "type",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.code}</Text>
        </Space>
      ),
    },
    {
      title: "Propriete",
      dataIndex: "property_name",
      key: "property_name",
      render: (value: string | null, record) =>
        value || `Propriete #${record.property_id}`,
    },
    {
      title: "Occupation",
      key: "occupancy",
      render: (_value, record) =>
        `${record.base_occupancy || 1}/${record.max_occupancy || 1}`,
    },
    {
      title: "Tarif par defaut",
      dataIndex: "default_rate",
      key: "default_rate",
      render: (value: number | null) => (value !== null ? value : "-") ,
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEditRoomTypes ? (
            <Button type="link" onClick={() => openEditRoomType(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteRoomTypes ? (
            <Button type="link" danger onClick={() => confirmDeleteRoomType(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  const amenityColumns: ColumnsType<HotelAmenity> = [
    {
      title: "Amenite",
      key: "amenity",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{record.category || "-"}</Text>
        </Space>
      ),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEditAmenities ? (
            <Button type="link" onClick={() => openEditAmenity(record)}>
              Modifier
            </Button>
          ) : null}
          {canDeleteAmenities ? (
            <Button type="link" danger onClick={() => confirmDeleteAmenity(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder a la configuration."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <>
      <Tabs defaultActiveKey="properties">
        <TabPane tab="Proprietes" key="properties">
          {canViewProperties ? (
            <Card
              title="Proprietes"
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchProperties}
                    loading={loadingProperties}
                  >
                    Rafraichir
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateProperty}
                    disabled={!canCreateProperties}
                  >
                    Nouvelle propriete
                  </Button>
                </Space>
              }
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Recherche">
                      <Input
                        placeholder="Nom, code, ville"
                        value={propertyFilters.search}
                        onChange={(event) =>
                          setPropertyFilters((prev) => ({
                            ...prev,
                            search: event.target.value,
                          }))
                        }
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Actif">
                      <Select
                        value={propertyFilters.isActive}
                        onChange={(value) =>
                          setPropertyFilters((prev) => ({
                            ...prev,
                            isActive: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          { value: "true", label: "Actif" },
                          { value: "false", label: "Inactif" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Table
                columns={propertyColumns}
                dataSource={filteredProperties}
                rowKey="id"
                loading={loadingProperties}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces aux proprietes."
              type="warning"
              showIcon
            />
          )}
        </TabPane>

        <TabPane tab="Batiments" key="buildings">
          {canViewBuildings ? (
            <Card
              title="Batiments"
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchBuildings}
                    loading={loadingBuildings}
                  >
                    Rafraichir
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateBuilding}
                    disabled={!canCreateBuildings || properties.length === 0}
                  >
                    Nouveau batiment
                  </Button>
                </Space>
              }
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={6}>
                    <Form.Item label="Propriete">
                      <Select
                        value={buildingFilters.propertyId}
                        onChange={(value) =>
                          setBuildingFilters((prev) => ({
                            ...prev,
                            propertyId: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Toutes" },
                          ...propertyOptions,
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Actif">
                      <Select
                        value={buildingFilters.isActive}
                        onChange={(value) =>
                          setBuildingFilters((prev) => ({
                            ...prev,
                            isActive: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          { value: "true", label: "Actif" },
                          { value: "false", label: "Inactif" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Table
                columns={buildingColumns}
                dataSource={buildings}
                rowKey="id"
                loading={loadingBuildings}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces aux batiments."
              type="warning"
              showIcon
            />
          )}
        </TabPane>

        <TabPane tab="Etages" key="floors">
          {canViewFloors ? (
            <Card
              title="Etages"
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchFloors}
                    loading={loadingFloors}
                  >
                    Rafraichir
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateFloor}
                    disabled={!canCreateFloors || properties.length === 0}
                  >
                    Nouvel etage
                  </Button>
                </Space>
              }
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={6}>
                    <Form.Item label="Propriete">
                      <Select
                        value={floorFilters.propertyId}
                        onChange={(value) =>
                          setFloorFilters((prev) => ({
                            ...prev,
                            propertyId: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Toutes" },
                          ...propertyOptions,
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Batiment">
                      <Select
                        value={floorFilters.buildingId}
                        onChange={(value) =>
                          setFloorFilters((prev) => ({
                            ...prev,
                            buildingId: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          ...buildingOptions,
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Actif">
                      <Select
                        value={floorFilters.isActive}
                        onChange={(value) =>
                          setFloorFilters((prev) => ({
                            ...prev,
                            isActive: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          { value: "true", label: "Actif" },
                          { value: "false", label: "Inactif" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Table
                columns={floorColumns}
                dataSource={floors}
                rowKey="id"
                loading={loadingFloors}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces aux etages."
              type="warning"
              showIcon
            />
          )}
        </TabPane>

        <TabPane tab="Types de chambre" key="room-types">
          {canViewRoomTypes ? (
            <Card
              title="Types de chambre"
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchRoomTypes}
                    loading={loadingRoomTypes}
                  >
                    Rafraichir
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateRoomType}
                    disabled={!canCreateRoomTypes || properties.length === 0}
                  >
                    Nouveau type
                  </Button>
                </Space>
              }
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={6}>
                    <Form.Item label="Propriete">
                      <Select
                        value={roomTypeFilters.propertyId}
                        onChange={(value) =>
                          setRoomTypeFilters((prev) => ({
                            ...prev,
                            propertyId: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Toutes" },
                          ...propertyOptions,
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Actif">
                      <Select
                        value={roomTypeFilters.isActive}
                        onChange={(value) =>
                          setRoomTypeFilters((prev) => ({
                            ...prev,
                            isActive: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          { value: "true", label: "Actif" },
                          { value: "false", label: "Inactif" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Table
                columns={roomTypeColumns}
                dataSource={roomTypes}
                rowKey="id"
                loading={loadingRoomTypes}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces aux types de chambre."
              type="warning"
              showIcon
            />
          )}
        </TabPane>

        <TabPane tab="Amenites" key="amenities">
          {canViewAmenities ? (
            <Card
              title="Amenites"
              extra={
                <Space>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={fetchAmenities}
                    loading={loadingAmenities}
                  >
                    Rafraichir
                  </Button>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={openCreateAmenity}
                    disabled={!canCreateAmenities}
                  >
                    Nouvelle amenite
                  </Button>
                </Space>
              }
            >
              <Form layout="vertical">
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={8}>
                    <Form.Item label="Recherche">
                      <Input
                        placeholder="Nom, categorie"
                        value={amenityFilters.search}
                        onChange={(event) =>
                          setAmenityFilters((prev) => ({
                            ...prev,
                            search: event.target.value,
                          }))
                        }
                        allowClear
                      />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={6}>
                    <Form.Item label="Actif">
                      <Select
                        value={amenityFilters.isActive}
                        onChange={(value) =>
                          setAmenityFilters((prev) => ({
                            ...prev,
                            isActive: value,
                          }))
                        }
                        options={[
                          { value: "all", label: "Tous" },
                          { value: "true", label: "Actif" },
                          { value: "false", label: "Inactif" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <Table
                columns={amenityColumns}
                dataSource={filteredAmenities}
                rowKey="id"
                loading={loadingAmenities}
                pagination={{ pageSize: 8 }}
              />
            </Card>
          ) : (
            <Alert
              message="Acces refuse"
              description="Vous n'avez pas acces aux amenites."
              type="warning"
              showIcon
            />
          )}
        </TabPane>
      </Tabs>

      <Modal
        title={editingProperty ? "Modifier la propriete" : "Nouvelle propriete"}
        open={propertyModalOpen}
        onCancel={() => {
          setPropertyModalOpen(false);
          setEditingProperty(null);
        }}
        onOk={handleSaveProperty}
        okText="Enregistrer"
      >
        <Form form={propertyForm} layout="vertical">
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Code requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Devise"
                name="currencyId"
                rules={[{ required: true, message: "Devise requise" }]}
              >
                <Select
                  options={currencies.map((currency) => ({
                    value: currency.id,
                    label: `${currency.code} - ${currency.name}`,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Établissement" name="warehouseId">
                <Select
                  allowClear
                  options={warehouses.map((warehouse) => ({
                    value: warehouse.id,
                    label: warehouse.name,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Classement" name="starRating">
                <InputNumber min={0} max={5} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Fuseau horaire" name="timezone">
                <Input placeholder="UTC" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Heure check-in" name="checkInTime">
                <Input placeholder="14:00:00" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Heure check-out" name="checkOutTime">
                <Input placeholder="12:00:00" />
              </Form.Item>
            </Col>
          </Row>
          <Divider />
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Email" name="email">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Telephone" name="phone">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Adresse" name="address">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Ville" name="city">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Pays" name="country">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Code postal" name="postalCode">
            <Input />
          </Form.Item>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingBuilding ? "Modifier le batiment" : "Nouveau batiment"}
        open={buildingModalOpen}
        onCancel={() => {
          setBuildingModalOpen(false);
          setEditingBuilding(null);
        }}
        onOk={handleSaveBuilding}
        okText="Enregistrer"
      >
        <Form form={buildingForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select options={propertyOptions} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Code requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingFloor ? "Modifier l'etage" : "Nouvel etage"}
        open={floorModalOpen}
        onCancel={() => {
          setFloorModalOpen(false);
          setEditingFloor(null);
        }}
        onOk={handleSaveFloor}
        okText="Enregistrer"
      >
        <Form form={floorForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select options={propertyOptions} />
          </Form.Item>
          <Form.Item label="Batiment" name="buildingId">
            <Select
              allowClear
              options={buildingOptionsForProperty(floorPropertyId)}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Numero d'etage"
                name="floorNumber"
                rules={[{ required: true, message: "Numero requis" }]}
              >
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Nom" name="name">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingRoomType ? "Modifier le type" : "Nouveau type"}
        open={roomTypeModalOpen}
        onCancel={() => {
          setRoomTypeModalOpen(false);
          setEditingRoomType(null);
        }}
        onOk={handleSaveRoomType}
        okText="Enregistrer"
        width={720}
      >
        <Form form={roomTypeForm} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select options={propertyOptions} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Code"
                name="code"
                rules={[{ required: true, message: "Code requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Occupation base" name="baseOccupancy">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Occupation max" name="maxOccupancy">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Tarif defaut" name="defaultRate">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Max adultes" name="maxAdults">
                <InputNumber min={1} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Max enfants" name="maxChildren">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Taille chambre" name="roomSize">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item label="Type de lit" name="bedType">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Vue" name="viewType">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Actif" name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Fumeur" name="smokingAllowed" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Accessible" name="accessible" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Amenites" name="amenityIds">
            <Select
              mode="multiple"
              allowClear
              options={amenities.map((amenity) => ({
                value: amenity.id,
                label: amenity.name,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingAmenity ? "Modifier l'amenite" : "Nouvelle amenite"}
        open={amenityModalOpen}
        onCancel={() => {
          setAmenityModalOpen(false);
          setEditingAmenity(null);
        }}
        onOk={handleSaveAmenity}
        okText="Enregistrer"
      >
        <Form form={amenityForm} layout="vertical">
          <Form.Item
            label="Nom"
            name="name"
            rules={[{ required: true, message: "Nom requis" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item label="Categorie" name="category">
            <Input />
          </Form.Item>
          <Form.Item label="Description" name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ConfigurationTab;
